import { RacemapColors } from '@racemap/utilities/consts/common';
import tinycolor from 'tinycolor2';

const MARKER_HEIGHT = 17;
const MARKER_WIDTH = 80;
const MARKER_PADDING = 0.5;
const MARKER_RADIUS = 4;
const TEXT_MARGIN_LEFT = 4;
const TEXT_PADDING = 2;
const INNER_CIRCLE_PADDING = 2;
const measureCanvas = document.createElement('canvas');
const measureCanvasContext = measureCanvas.getContext('2d');

interface Dimensions {
  width: number;
  height: number;
  radius: number;
  textPadding: number;
  markerPadding: number;
  bodyHeight: number;
  bodyWidth: number;
  textMarginLeft: number;
  textStartX: number;
  textStartY: number;
  textEndX: number;
  textEndY: number;
  centerInnerCircleX: number;
  centerInnerCircleY: number;
  innerCircleRadius: number;
}

function calculateDimensions({ resizeFactor = 1 }: { resizeFactor?: number }): Dimensions {
  if (!measureCanvasContext) throw new Error('Could not get 2d context');

  const height = MARKER_HEIGHT * resizeFactor;
  const width = MARKER_WIDTH * resizeFactor;
  const radius = MARKER_RADIUS * resizeFactor;
  const markerPadding = MARKER_PADDING * resizeFactor;
  const textPadding = TEXT_PADDING * resizeFactor;
  const textMarginLeft = TEXT_MARGIN_LEFT * resizeFactor;

  const bodyHeight = height - 2 * textPadding - 2 * markerPadding;
  const bodyWidth = width - 2 * textPadding - 2 * markerPadding;

  const centerInnerCircleX = markerPadding + textPadding + bodyHeight / 2;
  const centerInnerCircleY = height / 2;
  const innerCircleRadius = (bodyHeight - 2 * INNER_CIRCLE_PADDING) / 2;

  const textStartX = markerPadding + textPadding + bodyHeight + textMarginLeft;
  const textStartY = markerPadding + textPadding;
  const textEndX = width - markerPadding - radius;
  const textEndY = height - markerPadding - textPadding;

  return {
    width,
    height,
    radius,
    markerPadding,
    textPadding,
    bodyHeight,
    bodyWidth,
    textMarginLeft,
    textStartX,
    textStartY,
    textEndX,
    textEndY,
    centerInnerCircleX,
    centerInnerCircleY,
    innerCircleRadius,
  };
}

export interface GetTextLabelImageParams {
  bgColor?: string;
  borderColor?: string;
  alpha?: number;
  size?: number; // Resize factor for the image
  lineWidth?: number; // Optional line width for the border
}

export const getTextLabelImage = async ({
  bgColor = 'white',
  borderColor = RacemapColors.PaleBlue,
  alpha = 1,
  size = 1,
  lineWidth = 1, // Optional line width for the border
}: GetTextLabelImageParams): Promise<{ image: ImageBitmap; dimensions: Dimensions }> => {
  // Make the image not pixelized on devices with high DPR
  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  const resizeFactor = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2d context');

  const dimensions = calculateDimensions({ resizeFactor });
  const {
    width,
    height,
    radius,
    markerPadding,
    centerInnerCircleX,
    centerInnerCircleY,
    innerCircleRadius,
  } = dimensions;
  canvas.width = width * dpr;
  canvas.height = height * dpr;

  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  ctx.scale(dpr, dpr);

  ctx.globalAlpha = 1;
  ctx.fillStyle = tinycolor(bgColor).setAlpha(alpha).toRgbString();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = lineWidth * resizeFactor;

  ctx.beginPath();
  // start top/left corner
  ctx.moveTo(markerPadding, radius + markerPadding);
  // top/left round corner
  ctx.arc(radius + markerPadding, radius + markerPadding, radius, Math.PI, 1.5 * Math.PI, false);
  // top border
  ctx.lineTo(width - radius - markerPadding, markerPadding);
  // top/right round corner
  ctx.arc(width - radius - markerPadding, radius + markerPadding, radius, 1.5 * Math.PI, 0, false);
  // right border
  ctx.lineTo(width - markerPadding, height - radius - markerPadding);
  // bottom/right round corner
  ctx.arc(
    width - radius - markerPadding,
    height - radius - markerPadding,
    radius,
    0,
    0.5 * Math.PI,
    false,
  );
  // bottom border
  ctx.lineTo(radius + markerPadding, height - markerPadding);
  // bottom/left round corner
  ctx.arc(
    radius + markerPadding,
    height - radius - markerPadding,
    radius,
    0.5 * Math.PI,
    Math.PI,
    false,
  );
  // left border
  ctx.lineTo(markerPadding, radius + markerPadding);

  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Draw debug lines
  // drawDebugLine(
  //   [dimensions.textPadding + markerPadding, 0],
  //   [dimensions.textPadding + markerPadding, height],
  //   ctx,
  // );
  // drawDebugLine(
  //   [dimensions.textStartX - dimensions.textMarginLeft, 0],
  //   [dimensions.textStartX - dimensions.textMarginLeft, height],
  //   ctx,
  // );
  // drawDebugLine([0, dimensions.textStartY], [width, dimensions.textStartY], ctx);
  // drawDebugLine([dimensions.textStartX, 0], [dimensions.textStartX, height], ctx);
  // drawDebugLine([0, dimensions.textEndY], [width, dimensions.textEndY], ctx);
  // drawDebugLine([dimensions.textEndX, 0], [dimensions.textEndX, height], ctx);

  // Draw clear circle
  ctx.lineWidth = 0.8 * lineWidth * resizeFactor;
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(centerInnerCircleX, centerInnerCircleY, innerCircleRadius, Math.PI, 3 * Math.PI, false);
  ctx.fill();
  ctx.restore();

  // Stroke circle
  ctx.beginPath();
  ctx.arc(centerInnerCircleX, centerInnerCircleY, innerCircleRadius, Math.PI, 3 * Math.PI, false);
  ctx.closePath();
  ctx.stroke();

  // Stroke mini circle
  ctx.fillStyle = borderColor;
  ctx.lineWidth = 0.6 * lineWidth * resizeFactor;
  ctx.beginPath();
  ctx.arc(centerInnerCircleX, centerInnerCircleY, 1 * resizeFactor, Math.PI, 3 * Math.PI, false);
  ctx.closePath();
  ctx.stroke();

  const image = await createImageBitmap(canvas);
  return { image, dimensions };
};

// biome-ignore lint/correctness/noUnusedVariables: drawDebugLine is for debugging purposes
const drawDebugLine = (
  start: [number, number],
  end: [number, number],
  ctx: CanvasRenderingContext2D,
) => {
  const preStrokeStyle = ctx.strokeStyle;
  const preLineWidth = ctx.lineWidth;

  ctx.beginPath();
  ctx.moveTo(start[0], start[1]);
  ctx.lineTo(end[0], end[1]);
  ctx.lineWidth = 1;
  ctx.strokeStyle = RacemapColors.PaleBlue;
  ctx.setLineDash([2, 2]);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.strokeStyle = preStrokeStyle;
  ctx.lineWidth = preLineWidth;
};
